import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { MapPin } from 'lucide-react';
import { useNodeClickHandler } from './useNodeClickHandler';

export const LocationNode = memo(({ data, selected }) => {
  const handleClick = useNodeClickHandler(
    data.onViewDetails,
    () => ({
      type: 'location',
      name: data.label,
      description: data.description,
    }),
    data.isDraggingRef
  );

  return (
    <div
      className={`
        relative p-4 rounded-lg shadow-lg min-w-[180px] max-w-[220px] cursor-pointer
        bg-blue-950 border-2 border-blue-600
        ${selected ? 'ring-2 ring-white ring-offset-2 ring-offset-transparent' : ''}
        transition-all duration-200 hover:scale-105
      `}
      onClick={handleClick}
    >
      {/* Connection handles - larger hit area for easier connections */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-4 !h-4 !bg-blue-400 !border-2 !border-blue-600 hover:!bg-blue-300 hover:!scale-125 transition-transform"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-4 !h-4 !bg-blue-400 !border-2 !border-blue-600 hover:!bg-blue-300 hover:!scale-125 transition-transform"
      />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-4 !h-4 !bg-blue-400 !border-2 !border-blue-600 hover:!bg-blue-300 hover:!scale-125 transition-transform"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!w-4 !h-4 !bg-blue-400 !border-2 !border-blue-600 hover:!bg-blue-300 hover:!scale-125 transition-transform"
      />

      {/* Content */}
      <div className="flex flex-col gap-2">
        {/* Header with icon */}
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-blue-900">
            <MapPin className="w-5 h-5 text-blue-400" />
          </div>
          <span className="text-xs font-semibold text-blue-400 uppercase">
            Location
          </span>
        </div>

        {/* Name */}
        <p className="text-base font-bold text-white leading-tight">
          {data.label}
        </p>

        {/* Description preview */}
        {data.description && (
          <p className="text-sm text-gray-300 line-clamp-3">
            {data.description}
          </p>
        )}
      </div>

      {/* Location badge */}
      <div className="absolute -top-2 -right-2 bg-blue-500 rounded-full p-1">
        <MapPin className="w-3 h-3 text-white" />
      </div>
    </div>
  );
});

LocationNode.displayName = 'LocationNode';

export default LocationNode;
